
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { NewCandidateFormData } from '@/components/candidates/types';

interface CreateJobApplicationParams {
  jobId: string;
  candidateData?: NewCandidateFormData; // For new candidate
  candidateId?: string; // For existing candidate
  notes?: string;
}

export function useCreateJobApplication() {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createJobApplication = async ({ jobId, candidateData, candidateId: existingCandidateId, notes }: CreateJobApplicationParams) => {
    setIsCreating(true);
    setError(null);
    
    if (!candidateData && !existingCandidateId) {
      const noCandidateError = new Error('Either candidate data for a new candidate or an existing candidate ID must be provided.');
      setError(noCandidateError);
      setIsCreating(false);
      throw noCandidateError;
    }

    try {
      let finalCandidateId = existingCandidateId;

      if (candidateData) {
        // 1. Create the new candidate
        const { data: newCandidate, error: candidateError } = await supabase
          .from('candidates')
          .insert({
            first_name: candidateData.first_name,
            last_name: candidateData.last_name,
            email: candidateData.email,
            phone: candidateData.phone,
            source: candidateData.source,
            status: candidateData.status,
            // Ensure user_id is correctly sourced, assuming it's the authenticated user
            user_id: (await supabase.auth.getUser()).data.user?.id, 
          })
          .select('id')
          .single();
        
        if (candidateError) throw new Error(`Failed to create candidate: ${candidateError.message}`);
        if (!newCandidate) throw new Error('Failed to create candidate: No data returned');
        finalCandidateId = newCandidate.id;
      }

      if (!finalCandidateId) {
        throw new Error('Candidate ID is missing.');
      }

      // Check for existing application for this job and candidate
      const { count: existingApplicationCount, error: checkError } = await supabase
        .from('job_applications')
        .select('id', { count: 'exact' })
        .eq('job_id', jobId)
        .eq('candidate_id', finalCandidateId);

      if (checkError) {
        throw new Error(`Failed to check for existing applications: ${checkError.message}`);
      }

      if (existingApplicationCount && existingApplicationCount > 0) {
        throw new Error('This candidate has already applied for this job.');
      }
      
      // 2. Create the job application
      const { error: applicationError } = await supabase
        .from('job_applications')
        .insert({
          job_id: jobId,
          candidate_id: finalCandidateId,
          status: 'submitted', // Default status for a new application
          notes: notes || null,
          applied_at: new Date().toISOString(),
        });
      
      if (applicationError) throw new Error(`Failed to create application: ${applicationError.message}`);
      
      return { success: true, candidateId: finalCandidateId };
    } catch (err) {
      setError(err as Error);
      throw err; // Re-throw to be caught by the calling component
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createJobApplication,
    isCreating,
    error
  };
}
